import { Box } from "@mui/system";

const isEndOfMonth = (date: Date) => {
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return date.getDate() === lastDay.getDate();
};

const getDaysDiff = (start: Date, end: Date) => {
  const diff = end.getTime() - start.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
};

const isStartOfMonth = (date: Date) => date.getDate() === 1;

const isFullMonthRange = (start: Date, end: Date) => {
  return (
    isStartOfMonth(start) &&
    isEndOfMonth(end) &&
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  );
};

const isWeekStart = (date: Date) => date.getDay() === 0;


const getTimelineTicks = (startDate: string, endDate: string) => {
  const ticks: any[] = [];

  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalDays = getDaysDiff(start, end);

  const isFullMonth = isFullMonthRange(start, end);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const current = new Date(start);

  while (current <= end) {
    const tickDate = new Date(current);

    let showTick = false;
    let type = "none";

    // CASE 1: <= 14 days → DAILY VIEW
    if (totalDays <= 7) {
      const isRangeStart =
        tickDate.toDateString() === start.toDateString();
      const isRangeEnd =
        tickDate.toDateString() === end.toDateString();

      if (isRangeStart || isRangeEnd) {
        showTick = true;
        type = "day";
      }
    }

    // CASE 2: FULL MONTH
    else if (isFullMonth) {
      if (isStartOfMonth(tickDate) || isEndOfMonth(tickDate)) {
        showTick = true;
        type = "month-boundary";
      }
    }

    // CASE 3: 1+ MONTH → WEEK VIEW
    else if (totalDays <= 31) {
      const isRangeStart =
        tickDate.toDateString() === start.toDateString();
      const isRangeEnd =
        tickDate.toDateString() === end.toDateString();

      if (isRangeStart || isWeekStart(tickDate) || isStartOfMonth(tickDate) || isRangeEnd) {
        showTick = true;
        type = "week";
      }
    }

    // CASE 4: MULTI MONTH → EXISTING LOGIC
    else {
      if (isEndOfMonth(tickDate)) {
        showTick = true;
        type = "month-end";
      }

      // ALWAYS include range end (even if not month end)
      if (tickDate.toDateString() === end.toDateString() || tickDate.toDateString() === start.toDateString()) {
        showTick = true;
        type = "range-end";
      }
    }

    // Always include TODAY
    const isToday = tickDate.toDateString() === today.toDateString();

    ticks.push({
      date: tickDate,
      showTick,
      type,
      isToday,
    });

    current.setDate(current.getDate() + 1);
  }

  return ticks;
};

const formatLabel = (date: Date) =>
  date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    // year: "numeric",
  });

const TimelineHeader = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
  dayWidth?: number;
}) => {
  const ticks = getTimelineTicks(startDate, endDate);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  

  return (
    <Box>
      <Box
        sx={{
          height: "2px",
          bgcolor: "#43434D",
          width: "100%",
          position: "relative",
          zIndex: 1,
        }}
      />
      {/* Tick ruler */}
      <Box sx={{ display: "flex" }}>
        {ticks.map((tick, idx) => {
          const tickHeight = 10;
          let tickColor = "#43434D"; // default
          if (tick.isToday) tickColor = "#9478E4";
          const isLast = idx === ticks.length - 1;
          const showTick = tick.showTick || tick.isToday || isLast;
          const totalDays = ticks.length;
          const containerWidth = 1500; // ideally measured
          const dynamicDayWidth = containerWidth / totalDays;
          return (
            <Box
              key={idx}
              sx={{
                width: dynamicDayWidth,
                position: "relative",
                height: 10,
              }}
            >
              {showTick && (
                <Box
                  sx={{
                    position: "absolute",
                    left: isLast ? "auto" : 0,
                    right: isLast ? 0 : "auto",
                    width: "2px",
                    height: tickHeight,
                    bgcolor: tickColor,
                    borderRadius: 1,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Date labels */}
      <Box sx={{ display: "flex", position: "relative", height: 20,  }}>
        {ticks.map((tick, idx) => {
          const totalDays = ticks.length;
          const containerWidth = 1500; // ideally measured
          const dynamicDayWidth = containerWidth / totalDays;
          const previousVisibleTickIndex = ticks
            .slice(0, idx)
            .map((t, i) => ({ ...t, originalIndex: i }))
            .reverse()
            .find(t => t.showTick)?.originalIndex;

          const isCloseToPrevious =
            previousVisibleTickIndex !== undefined &&
            idx - previousVisibleTickIndex <= 3;

            const isMultiMonthTick = tick.type === "month-end";
            const isOneday = tick.type === "day"
        return (
          <Box
            key={idx}
            sx={{
              width: dynamicDayWidth,
              position: "relative", // tick box is relative
            }}
          >
            {tick.showTick || tick.isToday ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: (isCloseToPrevious && isMultiMonthTick) || idx === ticks.length - 1 ? "auto" : 0,
                  right: (isCloseToPrevious && isMultiMonthTick)|| idx === ticks.length - 1 ? 0 : "auto",
                  transform:
                  idx === 0 && !isOneday
                    ? "translateX(-90%)"
                    : isCloseToPrevious && isMultiMonthTick
                    ? "translateX(80%)"
                    : idx === ticks.length - 1
                    ? "translateX(90%)"
                    : idx === ticks.length - 2
                    ? "translateX(-90%)"
                    : "translateX(-50%)",
                  fontSize: 10,
                  color: "#575763",
                  whiteSpace: "nowrap",
                }}
              >
                {formatLabel(tick.date)}
              </Box>
            ) : null}
          </Box>
        )})}
      </Box>
    </Box>
  );
};

export default TimelineHeader;